<template>
  <AwTooltip v-if="syncDisabledTooltip" :text="syncDisabledTooltip" variant="white" :max-width="350">
    <AwButton size="md" color="secondary" :disabled="true" :is-loading="isLoading">
      <AwIcon name="i-ph-arrows-clockwise-duotone" class="size-3.5" />
      Synchroniser depuis Pappers
    </AwButton>
  </AwTooltip>
  <AwButton v-else size="md" color="secondary" :is-loading="isLoading" variant="outline" @click="syncFromPappers">
    <AwIcon name="i-ph-arrows-clockwise-duotone" class="size-3.5" />
    Synchroniser depuis Pappers
  </AwButton>
</template>

<script setup lang="ts">
import { useApi } from "~/composables/useApi";
import { useToast } from "~/composables/useToast";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { AwButton, AwIcon, AwTooltip } from "@asap/design";
import type { Company } from "@asap/shared/schemas/company";
import { canSyncCompanyFromPappers, getNextCompanyPappersSyncDate } from "@asap/shared/utils/company-sync";
import { usePosthog } from "~/composables/usePosthog";

const { invoke } = useApi();
const { addToastError, addToastSuccess } = useToast();
const posthog = usePosthog();

const isLoading = ref(false);

const emit = defineEmits<{
  onUpdate: [company: Company];
}>();

const props = defineProps<{
  company: Company;
}>();

const canSync = computed(() => props.company.siren !== null && canSyncCompanyFromPappers(props.company.last_synced_at));

const syncDisabledTooltip = computed(() => {
  if (canSync.value) {
    return null;
  }

  if (props.company.siren === null) {
    return "Un SIREN est requis pour synchroniser la société depuis Pappers";
  }

  if (props.company.last_synced_at === null) {
    return null;
  }

  const nextSyncDate = getNextCompanyPappersSyncDate(props.company.last_synced_at);
  const nextSyncDateLabel = format(nextSyncDate, "d MMMM yyyy", { locale: fr });

  return `La prochaine synchronisation Pappers sera possible à partir du ${nextSyncDateLabel}`;
});

const syncFromPappers = async () => {
  try {
    isLoading.value = true;
    const response = await invoke({
      path: "companies/:id/pappers-sync",
      method: "POST",
      params: { id: props.company.id },
    });

    if (response.success) {
      addToastSuccess({
        description: `La société ${props.company.name} a bien été synchronisée depuis Pappers`,
      });
      emit("onUpdate", response.data);
      posthog.trackEvent("company_pappers_sync_completed", { company_id: props.company.id, success: true });
    } else {
      addToastError({ title: "Une erreur est survenue lors de la synchronisation Pappers" }, response.error.message);
      posthog.trackEvent("company_pappers_sync_completed", { company_id: props.company.id, success: false });
    }
  } finally {
    isLoading.value = false;
  }
};
</script>
